'use client';
import React, { useState } from 'react';
import { Bell, X, AlertTriangle, ShieldAlert, Zap, Anchor, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


interface Alert {
  id: string;
  type: 'sanctions' | 'cyber' | 'maritime' | 'instability' | 'supply';
  title: string;
  body: string;
  severity: 'critical' | 'high' | 'medium';
  time: string;
  dismissed: boolean;
  href: string;
}

const initialAlerts: Alert[] = [
  { id: 'alert-001', type: 'sanctions', title: 'Sanctions Match — OFAC SDN', body: '2 Acme Corp suppliers exposed to new Iranian designations. Immediate screening required.', severity: 'critical', time: '14:07', dismissed: false, href: '/sanctions-tracker' },
  { id: 'alert-002', type: 'maritime', title: 'Red Sea Route Disruption', body: 'MV Stellaris rerouted via Cape of Good Hope. ETA +14 days. Insurance +22%.', severity: 'high', time: '13:54', dismissed: false, href: '/maritime-risk' },
  { id: 'alert-003', type: 'cyber', title: 'APT41 IOC Alert', body: '3 monitored IP ranges match APT41 IOCs. APAC financial ops at risk.', severity: 'high', time: '13:28', dismissed: false, href: '/cyber-risk' },
  { id: 'alert-004', type: 'instability', title: 'Taiwan Strait Escalation', body: 'PLA 72-hour exercise zone. 3 Acme Corp Taiwan suppliers at procurement risk.', severity: 'high', time: '13:41', dismissed: false, href: '/political-risk' },
  { id: 'alert-005', type: 'supply', title: 'Supplier Disruption — Chile', body: 'Codelco Andina halted. 3 Acme Corp copper procurement contracts affected.', severity: 'medium', time: '12:30', dismissed: false, href: '/supply-chain' },
  { id: 'alert-006', type: 'sanctions', title: 'EU Sanctions Package XIV', body: '14 new Russian energy entities designated. Cross-reference supplier list.', severity: 'high', time: '12:55', dismissed: false, href: '/sanctions-tracker' },
];

const alertIcons: Record<Alert['type'], React.ComponentType<{ size?: number; className?: string }>> = {
  sanctions: ShieldAlert,
  cyber: Zap,
  maritime: Anchor,
  instability: AlertTriangle,
  supply: AlertTriangle,
};

const alertSeverityStyle: Record<Alert['severity'], { border: string; icon: string; badge: string }> = {
  critical: { border: 'border-l-danger', icon: 'text-danger', badge: 'bg-danger/10 text-danger' },
  high: { border: 'border-l-warning', icon: 'text-warning', badge: 'bg-warning/10 text-warning' },
  medium: { border: 'border-l-info', icon: 'text-info', badge: 'bg-info/10 text-info' },
};

export default function AlertFeed() {
  const [alerts, setAlerts] = useState<Alert[]>(initialAlerts);
  const [showDismissed, setShowDismissed] = useState(false);

  const dismiss = (id: string) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, dismissed: true } : a));
  };

  const dismissAll = () => {
    setAlerts(prev => prev.map(a => ({ ...a, dismissed: true })));
  };

  const visible = alerts.filter(a => showDismissed ? true : !a.dismissed);
  const activeCount = alerts.filter(a => !a.dismissed).length;

  return (
    <div className="card-elevated rounded-xl border border-border flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Bell size={15} className="text-foreground" />
            {activeCount > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-danger rounded-full flex items-center justify-center">
                <span className="text-white font-bold" style={{ fontSize: '8px' }}>{activeCount}</span>
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-foreground">Alerts</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDismissed(v => !v)}
            className="text-2xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showDismissed ? 'Hide dismissed' : 'Show all'}
          </button>
          {activeCount > 0 && (
            <button
              onClick={dismissAll}
              className="text-2xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
            >
              <CheckCircle2 size={11} />
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Alert list */}
      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <CheckCircle2 size={24} className="text-success mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">All clear</p>
            <p className="text-xs text-muted-foreground">No active alerts for your monitored profile.</p>
          </div>
        ) : (
          visible.map((alert) => {
            const Icon = alertIcons[alert.type];
            const style = alertSeverityStyle[alert.severity];
            return (
              <div
                key={alert.id}
                className={`p-3 border-l-2 ${style.border} transition-all duration-200 ${
                  alert.dismissed ? 'opacity-40' : 'hover:bg-muted/30'
                }`}
              >
                <div className="flex items-start gap-2">
                  <Icon size={13} className={`${style.icon} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <Link href={alert.href} className="text-xs font-semibold text-foreground leading-snug truncate hover:text-primary transition-colors">
                        {alert.title}
                      </Link>
                      {!alert.dismissed && (
                        <button
                          onClick={() => dismiss(alert.id)}
                          className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                          title="Dismiss alert"
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>
                    <p className="text-2xs text-muted-foreground leading-relaxed mb-2">{alert.body}</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-2xs px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide ${style.badge}`}>
                        {alert.severity}
                      </span>
                      <span className="text-2xs text-muted-foreground font-tabular">{alert.time} UTC</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="p-3 border-t border-border flex-shrink-0">
        <Link href="/alerts" className="w-full text-xs text-center text-primary hover:text-primary/80 font-medium transition-colors py-1 block">
          View all alerts →
        </Link>
      </div>
    </div>
  );
}